import {
  organizationFeatureStrings,
  organizationManagementStrings,
} from 'constants/strings';
import { EOrganizationFeature } from 'types/enum';
import type { TOrganizationFeatures } from 'types/organization';

type FeatureSwitchProps = {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (checked: boolean) => void;
};

function FeatureSwitch({
  checked,
  disabled = false,
  ariaLabel,
  onChange,
}: FeatureSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
        checked ? 'bg-accent' : 'bg-neutral-300'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

type OrganizationFeatureFieldProps = {
  label: string;
  value: TOrganizationFeatures;
  onChange: (features: TOrganizationFeatures) => void;
  disabled?: boolean;
};

const ROW_CLASS =
  'flex items-center justify-between gap-3 text-xs font-semibold text-ink';

export function OrganizationFeatureField({
  label,
  value,
  onChange,
  disabled,
}: OrganizationFeatureFieldProps) {
  const strings = organizationManagementStrings.features;
  const labels = organizationFeatureStrings.labels;

  const toggle = (feature: EOrganizationFeature, checked: boolean) => {
    onChange({ ...value, [feature]: checked });
  };

  return (
    <div className="grid gap-2 text-xs font-semibold text-ink">
      <span>{label}</span>
      <div className="grid gap-2 rounded-xl border border-border/80 bg-white px-3 py-3">
        <div className={ROW_CLASS}>
          <span>{labels.tagManagement}</span>
          <FeatureSwitch
            ariaLabel={labels.tagManagement}
            checked={value.tagManagement}
            disabled={disabled}
            onChange={(checked) =>
              toggle(EOrganizationFeature.TAG_MANAGEMENT, checked)
            }
          />
        </div>
        {/* 一斉配信でタグつけはタグ管理の子。適用範囲が未確定のため常に操作不可で、値は保持する */}
        <div
          className={`${ROW_CLASS} pl-4 ${
            value.tagManagement ? '' : 'opacity-50'
          }`}
        >
          <span className="flex items-center gap-2 font-normal">
            <span aria-hidden="true">└</span>
            {labels.bulkSendTagging}
            <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold text-neutral-600">
              {strings.preparing}
            </span>
          </span>
          <FeatureSwitch
            ariaLabel={labels.bulkSendTagging}
            checked={value.bulkSendTagging}
            disabled
            onChange={(checked) =>
              toggle(EOrganizationFeature.BULK_SEND_TAGGING, checked)
            }
          />
        </div>
        <div className={ROW_CLASS}>
          <span>{labels.survey}</span>
          <FeatureSwitch
            ariaLabel={labels.survey}
            checked={value.survey}
            disabled={disabled}
            onChange={(checked) => toggle(EOrganizationFeature.SURVEY, checked)}
          />
        </div>
        <div className={ROW_CLASS}>
          <span>{labels.stepDelivery}</span>
          <FeatureSwitch
            ariaLabel={labels.stepDelivery}
            checked={value.stepDelivery}
            disabled={disabled}
            onChange={(checked) =>
              toggle(EOrganizationFeature.STEP_DELIVERY, checked)
            }
          />
        </div>
      </div>
      <p className="text-xs font-normal text-muted">{strings.note}</p>
    </div>
  );
}
